import { useSyncExternalStore } from 'react';

import { type V3ChatEntry } from '@apps/web/features/phoebe_chat_v3/model/transcript';

type Listener = () => void;

const EMPTY_ORDER: ReadonlyArray<string> = [];

export class V3RunStore {
    private entriesById = new Map<string, V3ChatEntry>();
    private durableOrder: ReadonlyArray<string> = EMPTY_ORDER;
    private liveOrder: ReadonlyArray<string> = EMPTY_ORDER;
    private order: ReadonlyArray<string> = EMPTY_ORDER;
    private readonly orderListeners = new Set<Listener>();
    private readonly entryListeners = new Map<string, Set<Listener>>();

    replace(entries: ReadonlyArray<V3ChatEntry>): void {
        this.replaceSegment('durable', entries);
    }

    replaceLive(entries: ReadonlyArray<V3ChatEntry>): void {
        this.replaceSegment('live', entries);
    }

    private replaceSegment(
        segment: 'durable' | 'live',
        entries: ReadonlyArray<V3ChatEntry>,
    ): void {
        const currentOrder =
            segment === 'durable' ? this.durableOrder : this.liveOrder;
        const otherOrder =
            segment === 'durable' ? this.liveOrder : this.durableOrder;
        const otherIds = new Set(otherOrder);
        const nextOrder: Array<string> = [];
        const nextIds = new Set<string>();
        const changedIds = new Set<string>();

        for (const candidate of entries) {
            if (nextIds.has(candidate.id) || otherIds.has(candidate.id)) {
                throw new Error(`Duplicate v3 chat entry id: ${candidate.id}`);
            }
            nextIds.add(candidate.id);
        }

        for (const candidate of entries) {
            const current = this.entriesById.get(candidate.id);
            const next =
                current?.kind === candidate.kind &&
                current.revision === candidate.revision
                    ? current
                    : candidate;
            this.entriesById.set(candidate.id, next);
            nextOrder.push(candidate.id);
            if (next !== current) {
                changedIds.add(candidate.id);
            }
        }

        for (const currentId of currentOrder) {
            if (!nextIds.has(currentId)) {
                this.entriesById.delete(currentId);
                changedIds.add(currentId);
            }
        }

        const orderChanged =
            currentOrder.length !== nextOrder.length ||
            currentOrder.some((id, index) => id !== nextOrder[index]);
        if (segment === 'durable') {
            this.durableOrder = orderChanged ? nextOrder : currentOrder;
        } else {
            this.liveOrder = orderChanged ? nextOrder : currentOrder;
        }
        if (orderChanged) {
            this.order = [...this.durableOrder, ...this.liveOrder];
            for (const listener of this.orderListeners) {
                listener();
            }
        }
        for (const id of changedIds) {
            for (const listener of this.entryListeners.get(id) ?? []) {
                listener();
            }
        }
    }

    getOrderSnapshot = (): ReadonlyArray<string> => this.order;

    subscribeOrder = (listener: Listener): (() => void) => {
        this.orderListeners.add(listener);
        return () => this.orderListeners.delete(listener);
    };

    getEntrySnapshot(id: string): V3ChatEntry | null {
        return this.entriesById.get(id) ?? null;
    }

    subscribeEntry(id: string, listener: Listener): () => void {
        const listeners = this.entryListeners.get(id) ?? new Set<Listener>();
        listeners.add(listener);
        this.entryListeners.set(id, listeners);
        return () => {
            listeners.delete(listener);
            if (listeners.size === 0) {
                this.entryListeners.delete(id);
            }
        };
    }
}

export function useV3EntryOrder(store: V3RunStore): ReadonlyArray<string> {
    return useSyncExternalStore(
        store.subscribeOrder,
        store.getOrderSnapshot,
        store.getOrderSnapshot,
    );
}

export function useV3Entry(store: V3RunStore, id: string): V3ChatEntry | null {
    return useSyncExternalStore(
        (listener) => store.subscribeEntry(id, listener),
        () => store.getEntrySnapshot(id),
        () => store.getEntrySnapshot(id),
    );
}
